/*
  Вкладка «Оплаты» карточки счёта (20.10.2026): оплаты всех источников - выписки банка,
  учётной системы, приставов и внесённые руками.

  Платежи не удаляются: у приложения нет права удалять деньги, и возврат уже идёт строкой
  с минусом (решение владельца 19.10.2026). Поэтому «Отменить» добавляет сторно: строку с
  минусом тем же месяцем и услугой, а долг возвращается в ту строку начислений, которую
  оплата уменьшила. В списке остаются обе записи - оплата помечена «отменена», сторно
  - «сторно».

  Отменить можно только оплату, внесённую руками (`can_remove` приходит с сервера):
  остальные ведёт сверка поступлений.
*/

import { useCallback, useState } from 'react'

import { api } from '../../api/client.js'
import { chargeMonth, date, money } from '../../api/format.js'
import { Empty, Loading, Notice, Tag } from '../Common.jsx'
import PageBar from '../PageBar.jsx'
import usePagedList from '../usePagedList.js'
import PaymentDialog from './PaymentDialog.jsx'
import RemoveDialog from './RemoveDialog.jsx'

function appliedTo(row) {
  // Прочерк читался как «ничего»; платёж без месяца в графу «Оплачено» расшифровки долга не входит
  // (аудит 26.10.2026, расхождение 2), и это называется словами.
  if (!row.applied_period) return 'не отнесена к месяцу'
  return `${chargeMonth(row.applied_period)} - ${row.applied_service}`
}

export default function PaymentsTab({ accountId, canEdit, onDone }) {
  const load = useCallback((window) => api.payments(accountId, window), [accountId])
  // Помесячные оплаты за годы с учётом сторно - список растёт с историей счёта; с
  // 21.10.2026 страницы режет сервер (требование владельца), здесь - общая панель.
  const { rows, error, reload, bar } = usePagedList(load)
  const [dialog, setDialog] = useState(null)

  const saved = (text) => () => {
    reload()
    onDone?.(text)
  }

  return (
    <section className="section card">
      <div className="row row--spread">
        <h2 className="section__title">Оплаты</h2>
        {canEdit ? (
          <button className="button" type="button" onClick={() => setDialog({ kind: 'add' })}>
            Внести оплату
          </button>
        ) : null}
      </div>

      <Notice error={error} />
      {rows === null && !error ? <Loading what="Загружаем оплаты" /> : null}
      {rows !== null && rows.length === 0 ? (
        <Empty title="Оплат пока нет">
          Оплаты приходят из выписки банка и учётной системы; без них их можно внести кнопкой
          выше.
        </Empty>
      ) : null}

      {rows !== null && rows.length > 0 ? (
        <table className="table">
          <thead>
            <tr>
              <th>Дата</th>
              <th className="table__num">Сумма</th>
              <th>Относится к</th>
              <th>Источник</th>
              <th>Плательщик</th>
              {canEdit ? <th /> : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.payment_id}>
                <td>{date(row.paid_on)}</td>
                <td className="table__num" data-label="Сумма">
                  {money(row.amount)}
                </td>
                <td data-label="Относится к">{appliedTo(row)}</td>
                <td data-label="Источник">
                  <div className="row row--tight">
                    <Tag kind="muted">{row.source_title}</Tag>
                    {row.is_reversal ? <Tag kind="warn">сторно</Tag> : null}
                    {row.reversed ? <Tag kind="warn">отменена</Tag> : null}
                  </div>
                </td>
                <td data-label="Плательщик">{row.payer ?? '-'}</td>
                {canEdit ? (
                  <td data-label="Действия">
                    {row.can_remove ? (
                      <button
                        className="button button--quiet"
                        type="button"
                        onClick={() => setDialog({ kind: 'reverse', payment: row })}
                      >
                        Отменить
                      </button>
                    ) : null}
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      <PageBar {...bar} what="оплат" />

      {dialog?.kind === 'add' ? (
        <PaymentDialog
          accountId={accountId}
          onSaved={saved('Оплата внесена.')}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === 'reverse' ? (
        <RemoveDialog
          title={`Отменить оплату от ${date(dialog.payment.paid_on)}`}
          action="Отменить оплату"
          onRemove={() => api.reversePayment(accountId, dialog.payment.payment_id)}
          onDone={saved('Оплата отменена: долг вернулся в строку начислений.')}
          onClose={() => setDialog(null)}
        >
          Сумма {money(dialog.payment.amount)}, {appliedTo(dialog.payment)}. Платежи не удаляются: в
          списке появится сторно, а долг вернётся в ту же строку начислений.
        </RemoveDialog>
      ) : null}
    </section>
  )
}
